import {
  PLAN_RUN_ROW_KINDS,
  PLAN_RUN_TRIGGERS,
  type PlanRunRowKindName,
  type PlanRunStatusName,
} from '@steado/admin-contract/plan-runs';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { ErrorState } from '@/components/shell/ErrorState';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { PaginationFooter } from '@/components/table/PaginationFooter';
import { ScopePills } from '@/components/table/ScopePills';
import { TableToolbar, type FilterSpec } from '@/components/table/TableToolbar';
import { BadgeCell, PersonCell, TextCell } from '@/components/table/cells';
import type { ToneName } from '@/components/table/tone';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDateTime, formatDuration, humanise } from '@/lib/format';

import { CronPanel } from './CronPanel';
import { DurationsPanel, WINDOW_DAYS } from './DurationsPanel';
import { PlanRunDrawer } from './PlanRunDrawer';
import { SpendPanel } from './SpendPanel';
import { PLAN_RUNS_PAGE_SIZE, planDurationsQueryOptions, planRunsQueryOptions } from './plan-ops-query';
import type { PlanOpsSearch } from './plan-ops-search';
import { planRunTone } from './plan-run-tone';

const COLUMNS: readonly TableColumn[] = [
  { key: 'user', label: 'Person', width: '1.2fr' },
  { key: 'kind', label: 'Plan', width: '0.7fr' },
  { key: 'week', label: 'Week', width: '0.9fr' },
  { key: 'trigger', label: 'Asked by', width: '0.9fr' },
  { key: 'status', label: 'Outcome', width: '1.5fr' },
  { key: 'passes', label: 'Attempt', width: '0.7fr' },
  { key: 'duration', label: 'Took', width: '0.7fr', align: 'right' },
  { key: 'started', label: 'Started', width: '0.9fr', align: 'right' },
];

/**
 * A meal plan's runs were invisible on this screen rather than separable, so
 * the kind is both a filter and a column: a mixed list that cannot say which
 * plan a row is for is a list of two populations.
 *
 * Purple is the console's Premium ink, and the meal plan is the Premium one.
 */
const KIND_TONES: Readonly<Record<PlanRunRowKindName, ToneName>> = {
  workouts: 'mute',
  meals: 'purple',
};

/**
 * The outcomes worth a pill of their own, and the only way the list filters on
 * one.
 *
 * Not every `PlanRunStatus`: `running` and `obsolete` are transient and nobody
 * opens this screen to look for them, and eight pills is a row nobody reads.
 * The rest are still reachable by `?status=` on the URL.
 */
type RunScopeId = PlanRunStatusName | 'all';

const RUN_SCOPES: readonly {
  readonly id: RunScopeId;
  readonly label: string;
  readonly status: PlanRunStatusName | undefined;
}[] = [
  { id: 'all', label: 'Every run', status: undefined },
  { id: 'failed', label: 'Failed', status: 'failed' },
  { id: 'provider_error', label: 'Provider error', status: 'provider_error' },
  { id: 'validation_failed', label: 'Validation failed', status: 'validation_failed' },
  { id: 'succeeded_with_residual', label: 'With a residual', status: 'succeeded_with_residual' },
];

export interface PlanOpsScreenProps {
  readonly search: PlanOpsSearch;
  readonly onSearchChange: (next: PlanOpsSearch) => void;
}

export function PlanOpsScreen({ search, onSearchChange }: PlanOpsScreenProps) {
  const { api } = useAuth();
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([]);

  const runs = useQuery(planRunsQueryOptions(api, search));
  // The pill counts. Also what `DurationsPanel` reads, so the two agree —
  // including on the kind, or a pill would count a population the table below
  // it is not showing.
  const durations = useQuery(planDurationsQueryOptions(api, WINDOW_DAYS, search.kind));

  const setFilter = useCallback(
    (patch: Partial<PlanOpsSearch>) => {
      setCursorStack([]);
      onSearchChange({ ...search, ...patch, cursor: undefined });
    },
    [search, onSearchChange],
  );

  const onNext = useCallback(() => {
    const next = runs.data?.pagination.nextCursor;
    if (next === undefined || next === null) return;
    setCursorStack((stack) => [...stack, search.cursor]);
    onSearchChange({ ...search, cursor: next });
  }, [runs.data, search, onSearchChange]);

  const onPrevious = useCallback(() => {
    setCursorStack((stack) => {
      onSearchChange({ ...search, cursor: stack[stack.length - 1] });
      return stack.slice(0, -1);
    });
  }, [search, onSearchChange]);

  const onOpenRun = useCallback(
    (run: string | undefined) => {
      onSearchChange({ ...search, run });
    },
    [search, onSearchChange],
  );

  // Outcome is not here: the scope pills below filter on the same `status`,
  // and they carry the counts a dropdown cannot.
  const filterSpecs: readonly FilterSpec[] = useMemo(
    () => [
      {
        id: 'kind',
        label: 'Kind',
        value: search.kind,
        options: PLAN_RUN_ROW_KINDS.map((value) => ({ value, label: humanise(value) })),
        onChange: (kind) => {
          setFilter({ kind: kind as PlanOpsSearch['kind'] });
        },
      },
      {
        id: 'trigger',
        label: 'Trigger',
        value: search.trigger,
        options: PLAN_RUN_TRIGGERS.map((value) => ({ value, label: humanise(value) })),
        onChange: (trigger) => {
          setFilter({ trigger: trigger as PlanOpsSearch['trigger'] });
        },
      },
    ],
    [search, setFilter],
  );

  const rows: readonly TableRow[] = useMemo(
    () =>
      (runs.data?.items ?? []).map((run) => ({
        id: run.id,
        onOpen: () => {
          onOpenRun(run.id);
        },
        cells: [
          <PersonCell
            key="user"
            name={run.displayName}
            sub={run.userId}
            initials={(run.displayName ?? '??').slice(0, 2).toUpperCase()}
          />,
          <BadgeCell key="kind" label={run.kind} tone={KIND_TONES[run.kind]} />,
          <TextCell key="week" value={run.weekStartDate} mono />,
          <TextCell key="trigger" value={humanise(run.trigger)} />,
          <BadgeCell key="status" label={humanise(run.status)} tone={planRunTone(run.status)} dot />,
          // 2 means the repair round ran, not that the week was retried — each
          // retry is its own row.
          <TextCell key="passes" value={String(run.attempt)} sub={run.attempt > 1 ? 'repaired' : null} />,
          <TextCell key="duration" value={formatDuration(run.durationMs)} align="right" />,
          <TextCell key="started" value={formatDateTime(run.startedAt)} align="right" />,
        ],
      })),
    [runs.data, onOpenRun],
  );

  const isFiltered =
    search.kind !== undefined ||
    search.status !== undefined ||
    search.trigger !== undefined ||
    search.userId !== undefined ||
    search.weekStartDate !== undefined;

  const onReset = useCallback(() => {
    setCursorStack([]);
    onSearchChange({});
  }, [onSearchChange]);

  const pageOffset = cursorStack.length * PLAN_RUNS_PAGE_SIZE;

  if (runs.isError) {
    return (
      <ErrorState
        title="Could not load plan runs"
        message={runs.error.message}
        onRetry={() => {
          void runs.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex max-w-[1240px] flex-col gap-3">
      {/*
        The toolbar leads, as it does on every other list screen. The two
        panels below it describe the population the table is drawn from, so
        they read as context for the search rather than as a separate screen
        stacked on top of one.
      */}
      <TableToolbar
        searchValue={search.search ?? ''}
        // Free text now: an operator arriving from a support thread has a name
        // or a run id, not a Monday date.
        searchPlaceholder="Subscriber or run id"
        onSearchChange={(value) => {
          setFilter({ search: value === '' ? undefined : value });
        }}
        filters={filterSpecs}
        isFiltered={isFiltered}
        onReset={onReset}
      />

      {/*
        Three panels in a two-column grid: the two tables stack in the wide
        column, where a row of figures is readable, and the cron reading sits
        beside them.
      */}
      <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-[1.35fr_1fr]">
        <DurationsPanel kind={search.kind} />
        <CronPanel />
        <SpendPanel kind={search.kind} />
      </div>

      <ScopePills
        label="Run scope"
        scopes={RUN_SCOPES.map((scope) => ({
          id: scope.id,
          label: scope.label,
          ...(scope.status === undefined
            ? {}
            : {
                count: durations.data?.byStatus.find((row) => row.status === scope.status)?.runs ?? 0,
              }),
        }))}
        value={search.status ?? 'all'}
        onChange={(next: RunScopeId) => {
          const scope = RUN_SCOPES.find((candidate) => candidate.id === next);
          setFilter({ status: scope?.status });
        }}
      />

      <DataTable
        caption="Plan generation runs"
        title={RUN_SCOPES.find((scope) => scope.id === (search.status ?? 'all'))?.label ?? 'Runs'}
        count={rows.length === 0 ? undefined : `showing ${String(rows.length)}`}
        columns={COLUMNS}
        rows={rows}
        isLoading={runs.isPending}
        emptyTitle={isFiltered ? 'No matches' : 'No runs yet'}
        emptyBody={
          isFiltered
            ? 'No generation run matches the current filters.'
            : 'No plan week has been generated yet.'
        }
        emptyAction={
          isFiltered ? (
            <Button variant="outline" onClick={onReset}>
              Clear filters
            </Button>
          ) : undefined
        }
        footer={
          <PaginationFooter
            rangeStart={rows.length === 0 ? 0 : pageOffset + 1}
            rangeEnd={pageOffset + rows.length}
            hasPrevious={cursorStack.length > 0}
            hasNext={runs.data?.pagination.hasMore ?? false}
            onPrevious={onPrevious}
            onNext={onNext}
            isLoading={runs.isPending}
          />
        }
      />

      {search.run === undefined ? null : (
        <PlanRunDrawer
          runId={search.run}
          onClose={() => {
            onOpenRun(undefined);
          }}
        />
      )}
    </div>
  );
}
